import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface GlobalProgressProps {
  isLoading: boolean;
}

/**
 * Global progress indicator shown at the top of the viewport
 * during React Query fetching/mutating operations.
 * 
 * Design: Dieter Rams - Industrial Refinement
 * - No decorative gradients or glow effects
 * - Simple, functional progress bar
 */
export const GlobalProgress: React.FC<GlobalProgressProps> = ({ isLoading }) => {
  const [progress, setProgress] = useState(0);
  const [visible, setVisible] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (isLoading) {
      // Show immediately and start progress
      setVisible(true);
      setProgress(15);
      
      // Gradual progress simulation
      const t1 = setTimeout(() => setProgress(35), 150);
      const t2 = setTimeout(() => setProgress(55), 400);
      const t3 = setTimeout(() => setProgress(75), 800);
      const t4 = setTimeout(() => setProgress(85), 1500);
      
      return () => {
        clearTimeout(t1);
        clearTimeout(t2);
        clearTimeout(t3);
        clearTimeout(t4);
      };
    } else if (visible) {
      // Complete and hide
      setProgress(100);
      timeoutRef.current = setTimeout(() => {
        setVisible(false);
        setProgress(0);
      }, 300);
      
      return () => {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
      };
    }
  }, [isLoading, visible]);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          className="fixed top-0 left-0 right-0 z-[9999] h-[3px] overflow-hidden"
          role="progressbar"
          aria-valuenow={progress}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label="Loading content"
        >
          {/* Background track */}
          <div className="absolute inset-0 bg-primary/10" />
          
          {/* Progress bar */}
          <motion.div
            className="h-full bg-primary relative"
            initial={{ width: 0 }}
            animate={{ width: `${progress}%` }}
            transition={{ 
              duration: progress === 100 ? 0.2 : 0.4, 
              ease: [0.25, 0.46, 0.45, 0.94] 
            }}
          />
        </motion.div>
      )}
    </AnimatePresence>
  );
};
